import {
  call,
  put,
  takeEvery,
  all,
} from 'redux-saga/effects';
import API from 'library/constants/api';
import request from 'library/helpers/request';
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from 'store/shared/actions';
import * as constants from './constants';
import * as actions from './actions';
import {
  getProjectItems,
  resetProjectItems,
  resetProjectItemsActions,
  setProjectItemsFilters,
} from '../project-items/actions';
import { getBrowseItems, resetBrowseItemsActions } from '../browse-items/actions';
import {
  getWishlistItems,
  resetWishlistItems as resetWishlistItemsAction,
  resetWishlistItemsActions,
} from '../wishlist-items/actions';

function* getProject(action) {
  try {
    const { data } = yield call(request, `${API.PROJECTS}/${action.payload.id}`);

    yield put(actions.getProjectSuccess(data));
  } catch (e) {
    yield put(actions.getProjectError(e));
    yield put(showErrorNotification({ message: 'Failed to fetch data, please try later.' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* getProjectWatcher() {
  yield takeEvery(constants.GET_PROJECT, getProject);
}

function* deleteProject(action) {
  const {
    id,
    shouldUpdate = true,
  } = action.payload;

  try {
    yield call(request, `${API.PROJECTS}/${id}`, 'DELETE');

    yield put(actions.deleteProjectsSuccess());

    if (shouldUpdate) {
      yield put(getProjectItems());
    }

    yield put(showSuccessNotification({ message: 'Project successfully deleted' }));
  } catch (e) {
    yield put(actions.deleteProjectError(e));
    yield put(showErrorNotification({ message: 'Failed to fetch data, please try later.' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* deleteProjectWatcher() {
  yield takeEvery(constants.DELETE_PROJECT, deleteProject);
}

function* deleteProjects(action) {
  try {
    yield put(showWarningNotification({ message: 'Delete in progress.', isLoading: false }));
    // todo - to be removed after api integration as json web server do not support aggregation
    // so that mock api contains separate table for project items
    yield all(action.payload.ids.map((id) => call(deleteProject, id)));

    yield put(actions.deleteProjectsSuccess());
    yield put(showSuccessNotification({ message: 'Delete success.' }));
    yield put(setProjectItemsFilters({}));
    yield put(getProjectItems());
  } catch (e) {
    yield put(actions.deleteProjectsError(e));
    yield put(showErrorNotification({ message: 'Failed to delete data, please try later.' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* deleteProjectsWatcher() {
  yield takeEvery(constants.DELETE_PROJECTS, deleteProjects);
}

function* createProject(action) {
  const {
    data,
    // shouldUpdate = true
  } = action.payload;
  try {
    yield call(request, `${API.PROJECTS}`, 'POST', { data });
    yield put(showSuccessNotification({ message: 'New project successfully created' }));
    yield put(resetProjectItems());
    yield put(resetProjectItemsActions());
    // yield put(resetBrowseItems());
    yield put(getProjectItems());
    yield put(actions.createProjectSuccess());
  } catch (e) {
    yield put(actions.createProjectError(e));
    yield put(showErrorNotification({ message: 'Oops :(' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* createProjectWatcher() {
  yield takeEvery(constants.CREATE_PROJECT, createProject);
}

function* batchAddToProject(action) {
  const {
    ids = [],
    type,
    colors,
    id,
    messages,
    resetBrowseItems = false,
    resetProjects = false,
    resetProject = false,
    resetWishlistItems = false,
  } = action.payload;
  try {
    const data = { type };

    if (ids.length) {
      data.ids = ids;
    } else if (colors.length) {
      data.colors = colors;
    }

    if (ids.length || colors.length) {
      yield call(request, `${API.PROJECTS}/${id}/add`, 'PATCH', { data });
      yield put(showSuccessNotification({ message: messages.success }));
    }

    yield put(actions.batchAddToProjectSuccess());

    if (resetProjects) {
      yield put(resetProjectItems());
      yield put(resetProjectItemsActions());
      yield put(getProjectItems());
    }

    if (resetProject) {
      yield put(actions.resetProject());
      yield put(actions.getProject({ id }));
    }

    if (resetBrowseItems) {
      yield put(resetBrowseItemsActions());
      yield put(getBrowseItems());
    }

    if (resetWishlistItems) {
      yield put(resetWishlistItemsAction());
      yield put(getWishlistItems());
      yield put(resetWishlistItemsActions());
    }
  } catch (e) {
    yield put(actions.batchAddToProjectError(e));
    yield put(showErrorNotification({ message: messages.error }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* batchAddToProjectWatcher() {
  yield takeEvery(constants.BATCH_ADD_TO_PROJECT, batchAddToProject);
}

function* editProject(action) {
  const {
    data,
    id,
    // shouldUpdate = true
  } = action.payload;
  try {
    yield call(request, `${API.PROJECTS}/${id}`, 'PATCH', { data });
    yield put(showSuccessNotification({ message: 'Project successfully updated' }));
    yield put(actions.getProject({ id }));
  } catch (e) {
    yield put(showErrorNotification({ message: 'Oops :(' }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* editProjectWatcher() {
  yield takeEvery(constants.EDIT_PROJECT, editProject);
}

function* batchRemoveFromProject(action) {
  const {
    ids = [],
    type,
    id,
    messages,
    resetBrowseItems = false,
    resetProjects = false,
    resetProject = false,
  } = action.payload;
  try {
    const data = { type };

    if (ids.length && type !== 'COLORS') {
      data.ids = ids;
    } else if (type === 'COLORS') {
      data.colors = ids;
    }

    if (data?.ids?.length || data?.colors?.length) {
      yield call(request, `${API.PROJECTS}/${id}/remove`, 'PATCH', { data });
      yield put(showSuccessNotification({ message: messages.success }));
    }

    yield put(actions.batchRemoveFromProjectSuccess());

    if (resetBrowseItems) {
      yield put(resetBrowseItemsActions());
      yield put(getBrowseItems());
    }

    if (resetProjects) {
      yield put(resetProjectItems());
      yield put(resetProjectItemsActions());
      yield put(getProjectItems());
    }

    if (resetProject) {
      yield put(actions.resetProjectEntityData());
      yield put(actions.getProject({ id }));
    }
  } catch (e) {
    yield put(actions.batchAddToProjectError(e));
    yield put(showErrorNotification({ message: messages.error }));
  }
}

// eslint-disable-next-line import/prefer-default-export
export function* batchRemoveFromProjectWatcher() {
  yield takeEvery(constants.BATCH_REMOVE_FROM_PROJECT, batchRemoveFromProject);
}
